import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import {
  ReferenceEntity,
  ReferenceObligation,
} from 'src/app/static-data/models/interfaces/static-data';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { pairwise, startWith, takeUntil } from 'rxjs/operators';
import { FormConfigService } from 'src/app/form-config/form-config.service';
import { APPLICABLE } from 'src/app/product-capture/model/consts/applicable.const';
import { PERFORMANCE_TYPES_REFERENCE } from 'src/app/product-capture/model/consts/performance-type-reference-modal.const';
import { PRODUCT_CAPTURE_CONST } from 'src/app/product-capture/model/consts/product-capture-key.const';
import { SETTLEMENT_TYPE_REFERENCE } from 'src/app/product-capture/model/consts/settlement-type-reference-modal.const';
import { ReferenceEntityEnum } from 'src/app/product-capture/model/enums/product-capture.enums';
import { DropDownOption } from 'src/app/product-capture/model/interfaces/drop-down-option.interface';
import { ReferenceEntityInterface } from 'src/app/product-capture/model/interfaces/reference-entity.interface';
import { DcSelectComponent } from 'src/app/ui-kit/dc-select/dc-select.component';
import { FilterEntitiesPipe } from './pipes/filter-entities.pipe';
import { ReferenceEntitiesComponent } from './reference-entities/reference-entities.component';
import { PERFORMANCE_TYPE_KEYS } from 'src/app/product-capture/model/consts/performance-type.const';

@Component({
  selector: 'app-reference-modal',
  templateUrl: './reference-modal.component.html',
  styleUrls: ['./reference-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ReferenceModalComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  labels: Record<string, any>;
  performanceTypeItems = PERFORMANCE_TYPES_REFERENCE;
  settlementTypeItems = SETTLEMENT_TYPE_REFERENCE;
  standardObligationsItems = APPLICABLE;
  referenceObligationOptions: { [key: string]: DropDownOption[] } = {};
  referenceEntityOptions: { [key: string]: DropDownOption[] } = {};
  referenceObligationItems: ReferenceObligation[] = [];
  referenceEntityItems: ReferenceEntity[] = [];
  productId: number;
  selectedEntitiesIds: number[] = [];

  @ViewChild(ReferenceEntitiesComponent)
  referenceEntitiesComponent: ReferenceEntitiesComponent;

  private destroy$: Subject<any>;

  constructor(
    private fb: UntypedFormBuilder,
    private formConfigService: FormConfigService,
    public modal: NgbActiveModal
  ) {
    this.formConfigService.setFormConfig(PRODUCT_CAPTURE_CONST);
    this.form = new UntypedFormGroup({
      performanceType: new UntypedFormControl(),
      settlementType: new UntypedFormControl(),
      referenceEntities: new UntypedFormArray([]), // TODO rename later
    });

    this.destroy$ = new Subject();
  }

  ngOnInit(): void {
    this.labels = this.formConfigService.getLabels();
  }

  ngAfterViewInit(): void {
    this.prepareReferenceEntities();
    this.referenceEntitiesComponent.referencesSelections.changes
      .pipe(takeUntil(this.destroy$))
      .subscribe((_) => {
        this.prepareReferenceEntities();
      });

    this.referenceEntities.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((referenceEntities) =>
        this.performanceType.setValue(
          this.getPerformanceType(referenceEntities)
        )
      );
  }

  prepareReferenceEntities(): void {
    const selections =
      this.referenceEntitiesComponent?.referencesSelections.filter(
        (field: DcSelectComponent) =>
          field.formControlName === 'nonStandardReferenceObligationsList'
      );

    selections.forEach((selectionField: DcSelectComponent, index: number) => {
      const { value: entityId } =
        this.referenceEntities.controls[index].get('referenceEntityId');
      selectionField.items = this.getObligationOptions(entityId);
      selectionField.detectChanges();
    });
  }

  get performanceType(): UntypedFormControl {
    return this.form.get(
      ReferenceEntityEnum.performanceType
    ) as UntypedFormControl;
  }

  get settlementType(): UntypedFormControl {
    return this.form.get(
      ReferenceEntityEnum.settlementType
    ) as UntypedFormControl;
  }

  get referenceEntities(): UntypedFormArray {
    return this.form.get('referenceEntities') as UntypedFormArray;
  }

  addReference(value: ReferenceEntityInterface | null, index: number): void {
    this.addRowData(value);
    this.nullifyRow0Weight(
      index,
      this.referenceEntities?.at(0)?.get('weight')?.value
    );

    this.referenceEntityOptions[index.toString()] =
      FilterEntitiesPipe.getEntityOptions(this.referenceEntityItems);
    this.referenceObligationOptions[index.toString()] =
      this.getObligationOptions(null);
  }

  generateRows(groupData: ReferenceEntityInterface[] | []): void {
    if (groupData?.length) {
      groupData.forEach((asset) => {
        this.addRowData(asset);
      });
    } else {
      this.addRowData(null);
      this.referenceEntities.at(0).get('weight').setValue(1);
    }
  }

  nullifyRow0Weight(index: number, row0WeightValue: number | string): void {
    if (index === 1 && (row0WeightValue === 1 || row0WeightValue === '1')) {
      this.referenceEntities.at(0).get('weight').setValue('');
    } else return;
  }

  addRowData(value: ReferenceEntityInterface): void {
    const reference = this.fb.group({
      referenceEntityId: [value?.referenceEntityId || ''],
      weight: [value?.weight ?? ''],
      seniorityLevel: [{ value: value?.seniorityLevel, disabled: true }],
      transactionType: [{ value: value?.transactionType, disabled: true }],
      standardReferenceObligations: [
        value?.standardReferenceObligations ?? true,
      ],
      nonStandardReferenceObligationsList: [
        value?.nonStandardReferenceObligationsList || [],
      ],
    });

    reference
      .get('referenceEntityId')
      .valueChanges.pipe(startWith(-1), takeUntil(this.destroy$), pairwise())
      .subscribe(([prevEntityId, entityId]: number[]) => {
        const referenceEntity = this.referenceEntityItems.filter(
          (entity) => entity.referenceEntityID === entityId
        )[0];

        if (String(prevEntityId)?.trim().length && prevEntityId > -1) {
          const indexOfPrev = this.selectedEntitiesIds.indexOf(prevEntityId);
          this.selectedEntitiesIds.splice(indexOfPrev, 1);
        }

        if (referenceEntity) {
          const {
            seniorityLevel: seniorityField,
            transactionType: transactionTypeField,
          } = reference.controls;
          const { seniorityLevel, transactionType } = referenceEntity;

          seniorityField.setValue(seniorityLevel);
          transactionTypeField.setValue(transactionType);

          if (prevEntityId !== -1) {
            this.selectedEntitiesIds.push(entityId);
          }
          this.updateReferenceEntityFields();
        }
      });

    this.referenceEntities.push(reference);
    reference
      .get('referenceEntityId')
      .updateValueAndValidity({ onlySelf: true, emitEvent: true });

    this.queueTask(() => {
      if (
        value?.referenceEntityId &&
        this.selectedEntitiesIds.indexOf(value.referenceEntityId) === -1
      ) {
        this.selectedEntitiesIds.push(value.referenceEntityId);
      }
      this.updateReferenceEntityFields();
    });
  }

  queueTask(callback: Function): void {
    setTimeout(() => callback?.());
  }

  getObligationSelectOptions(index: number): DropDownOption[] {
    const key = index.toString();

    if (!this.referenceObligationOptions.hasOwnProperty(key)) {
      this.referenceObligationOptions[key] = this.getObligationOptions(null);
    }

    return this.referenceObligationOptions[key];
  }

  deleteReference(index: number): void {
    const id = this.referenceEntities.controls[index]?.value.referenceEntityId;

    if (this.referenceEntities.value.length <= 1) {
      this.referenceEntities.reset();
      this.referenceObligationOptions['0'] = this.getObligationOptions(null);
    } else {
      this.referenceEntities.removeAt(index);
    }

    this.removeSelectedEntityById(id);
  }

  removeSelectedEntityById(id: number): void {
    if (!Number.isInteger(id) || !String(id)?.trim().length) {
      return;
    }

    const indexOfEntity = this.selectedEntitiesIds.indexOf(id);
    this.selectedEntitiesIds.splice(indexOfEntity, 1);

    this.updateReferenceEntityFields();
  }

  updateReferenceEntityFields(): void {
    this.selectedEntitiesIds = [...this.selectedEntitiesIds];
  }

  private getPerformanceType(referenceEntities: any[]): string {
    if (!referenceEntities || referenceEntities.length <= 1) {
      return PERFORMANCE_TYPE_KEYS.SINGLE;
    }

    if (
      referenceEntities.filter((entity) =>
        Number.isInteger(entity.referenceEntityId)
      ).length > 1
    ) {
      return PERFORMANCE_TYPE_KEYS.BASKET;
    }

    return PERFORMANCE_TYPE_KEYS.SINGLE;
  }

  setDialogProps(props: any): void {
    this.performanceType.setValue(
      this.getPerformanceType(props.referenceEntities)
    );
    this.settlementType.setValue(props.settlementType);

    this.referenceEntityItems = props.referenceEntityItems;
    this.referenceObligationItems = props.referenceObligationItems;
    this.productId = props.productId;

    this.generateRows(props.referenceEntities);
  }

  clearModal(): void {
    this.modal.close('cancel');
  }

  save(): void {
    let { referenceEntities, ...rest } = this.form.value;
    referenceEntities = referenceEntities.filter(
      (referenceEntity: ReferenceEntityInterface) =>
        Number.isInteger(referenceEntity.referenceEntityId)
    );
    const filteredEntities = {
      ...rest,
      referenceEntities,
    };
    this.modal.close(filteredEntities);
  }

  selectEntity(
    entityId: number,
    formGroup: UntypedFormGroup,
    index: number,
    fromObligation = false
  ): void {
    const key = index.toString();

    if (!fromObligation) {
      formGroup.get('nonStandardReferenceObligationsList').setValue(null);
    }

    this.referenceObligationOptions[key] = this.getObligationOptions(entityId);
  }

  selectObligation(formGroup: UntypedFormGroup, index: number): void {
    const obligationId =
      formGroup.get('nonStandardReferenceObligationsList').value[0] || null;

    if (obligationId === null) {
      return;
    }

    const obligation = this.referenceObligationItems.find(
      (item) => item.referenceObligationID === obligationId
    );

    const referenceEntityField = formGroup.get('referenceEntityId');
    const currentReferenceEntityId = referenceEntityField.value;

    if (obligation?.referenceEntityID !== currentReferenceEntityId) {
      formGroup.get('referenceEntityId').setValue(obligation.referenceEntityID);
      this.selectEntity(obligation?.referenceEntityID, formGroup, index, true);
    }
  }

  private getObligationOptions(entityId: number | null): DropDownOption[] {
    let options = this.referenceObligationItems.filter(
      (item: ReferenceObligation) =>
        entityId === null ||
        (entityId !== item.referenceEntityID &&
          !this.selectedEntitiesIds.includes(item.referenceEntityID)) ||
        entityId === item.referenceEntityID
    );

    const getObligation = (obligation: ReferenceObligation) => {
      const displayValue = obligation.isin
        ? `${obligation.longName} | ${obligation.isin}`
        : obligation.longName;
      return {
        id: obligation.referenceObligationID,
        displayValue: displayValue,
      } as DropDownOption;
    };

    const getOptions = (list: ReferenceObligation[]) =>
      list.map((obligation) => getObligation(obligation));

    if (entityId) {
      options = options.filter(
        (obligation) => obligation.referenceEntityID === entityId
      );
    }

    return getOptions(options);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }
}
